// src/pages/Seguimiento.jsx
import Cargando from "../components/Cargando";
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useCotizaciones } from "../hooks/useCotizaciones";
import { useClientes } from "../hooks/useClientes";
import { usePedidos } from "../hooks/usePedidos";
import { crearActividadCliente } from "../hooks/useActividadesCliente";
import { agregarNotaCotizacion } from "../hooks/useNotasCotizacion";
import { Link } from "react-router-dom";
import { updateDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import toast from "react-hot-toast";
import { fechaHoyLocal, diasDesdeHoy, parsearFechaLocal } from "../utils/fechas";

const TIPOS_ACTIVIDAD = [
  { key: "Llamada",        icon: "📞" },
  { key: "WhatsApp",       icon: "💬" },
  { key: "Email",          icon: "📧" },
  { key: "Reunión",        icon: "🤝" },
  { key: "Visita en obra", icon: "🏗" },
  { key: "Otro",           icon: "📝" },
];

const ESTADOS_COT = ["Pendiente"];

const ESTADO_STYLE = {
  "Pendiente":   { bg: "#E6F1FB", color: "#185FA5" },
  "Aprobada":    { bg: "#EAF3DE", color: "#3B6D11" },
  "Rechazada":   { bg: "#FCEBEB", color: "#A32D2D" },
  "Vencida":     { bg: "#FAEEDA", color: "#854F0B" },
  "Ganada":      { bg: "#E1F5EE", color: "#0F6E56" },
  "Perdida":     { bg: "#FCEBEB", color: "#A32D2D" },
};

const AV = [
  { bg: "#B5D4F4", color: "#0C447C" },
  { bg: "#9FE1CB", color: "#085041" },
  { bg: "#CECBF6", color: "#3C3489" },
  { bg: "#FAC775", color: "#633806" },
  { bg: "#F5C4B3", color: "#712B13" },
];

function avColor(nombre) { return AV[(nombre?.charCodeAt(0) || 0) % AV.length]; }

function calcDias(fecha) {
  return diasDesdeHoy(fecha);
}

// Normaliza Timestamp de Firestore, string "YYYY-MM-DD" o Date a un objeto Date local.
function fechaAObjeto(val) {
  if (!val) return null;
  if (val.toDate) return val.toDate();
  if (typeof val === "string") return parsearFechaLocal(val) || new Date(val);
  return new Date(val);
}

// Días transcurridos desde una fecha (positivo = en el pasado).
function diasTranscurridos(val) {
  const d = fechaAObjeto(val);
  if (!d) return null;
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((hoy - d) / 86400000);
}

function fmtFecha(fecha) {
  if (!fecha) return "—";
  const [y, m, d] = fecha.split("-");
  return `${d}/${m}/${y}`;
}

function labelDias(dias) {
  if (dias === null) return "";
  if (dias < 0)   return `Vencido hace ${Math.abs(dias)}d`;
  if (dias === 0) return "Hoy";
  if (dias === 1) return "Mañana";
  return `En ${dias} días`;
}

function fmtMoney(v) { return `S/ ${Number(v || 0).toLocaleString("es-PE")}`; }

// ── Tarjeta de cotización (sin cambios) ──────────────────────────────────────
function CotizacionCard({ cot, cliente, onWA, onEmail, onRealizado }) {
  const dias = cot._dias;
  const av   = avColor(cot.clienteNombre);
  const es   = ESTADO_STYLE[cot.estado] || ESTADO_STYLE["Nueva"];

  const franjaColor = dias < 0 ? "#DC2626" : dias === 0 ? "#D97706" : "#1D9E75";
  const fbg         = dias < 0 ? "#FCEBEB" : dias === 0 ? "#FAEEDA" : "#E1F5EE";
  const fcol        = dias < 0 ? "#A32D2D" : dias === 0 ? "#854F0B" : "#0F6E56";

  return (
    <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 12, marginBottom: 8, overflow: "hidden", display: "flex", alignItems: "stretch" }}>
      <div style={{ width: 4, flexShrink: 0, background: franjaColor }} />
      <div style={{ flex: 1, padding: "12px 16px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: av.bg, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
          {(cot.clienteNombre || "?").charAt(0).toUpperCase()}
        </div>

        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 3 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{cot.clienteNombre}</span>
            {cot.numeroCot && (
              <span style={{ fontSize: 10, fontWeight: 700, color: "#3A3C7A", background: "#EEF2FF", padding: "1px 7px", borderRadius: 5 }}>
                {cot.numeroCot}
              </span>
            )}
            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: es.bg, color: es.color }}>
              {cot.estado}
            </span>
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>
            {cot.clienteEmpresa || ""}{cot.proyecto ? ` · ${cot.proyecto}` : ""}{cot.responsable ? ` · ${cot.responsable}` : ""}
          </div>
          {cot.notas && (
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 3, fontStyle: "italic" }}>📝 {cot.notas}</div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, minWidth: 90 }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#1e293b" }}>{fmtFecha(cot.proximoSeguimiento)}</span>
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: fbg, color: fcol }}>
            {labelDias(dias)}
          </span>
        </div>

        {cot.valor > 0 && (
          <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", minWidth: 80, textAlign: "right" }}>
            {fmtMoney(cot.valor)}
          </div>
        )}

        <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
          <button onClick={() => onWA(cliente?.telefono, cot.clienteNombre)}
            style={{ fontSize: 11, fontWeight: 600, padding: "6px 12px", borderRadius: 8, background: "#DCF8C6", color: "#128C7E", border: "none", cursor: "pointer" }}>
            WhatsApp
          </button>
          <button onClick={() => onEmail(cliente?.email, cot.clienteNombre)}
            style={{ fontSize: 11, fontWeight: 600, padding: "6px 12px", borderRadius: 8, background: "#E6F1FB", color: "#185FA5", border: "none", cursor: "pointer" }}>
            Email
          </button>
          <button onClick={() => onRealizado(cot)}
            style={{ fontSize: 11, fontWeight: 600, padding: "6px 12px", borderRadius: 8, background: "#1E3A5F", color: "#fff", border: "none", cursor: "pointer" }}>
            ✓ Realizado
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function Seguimiento() {
  const { cotizaciones, loading, actualizarCotizacion } = useCotizaciones();
  const { clientes, actualizarCliente } = useClientes();
  const { pedidos } = usePedidos();

  const [tabPrincipal,      setTabPrincipal]      = useState("cotizaciones");
  const [filtroTiempo,      setFiltroTiempo]      = useState("todos");
  const [filtroEstado,      setFiltroEstado]      = useState("todos");
  const [filtroResponsable, setFiltroResponsable] = useState("todos");
  const [busqueda,          setBusqueda]          = useState("");
  const [filtroTag,         setFiltroTag]         = useState("");
  const [paginaCot,         setPaginaCot]         = useState(1);

  // Modal Realizado — cotización (guarda nota + actualiza proximoSeguimiento en cot)
  const [modalRealizado, setModalRealizado] = useState(null);
  const [nuevaFecha,     setNuevaFecha]     = useState("");
  const [notaRealizado,  setNotaRealizado]  = useState("");

  // Modal Realizado — cliente (crea actividad + actualiza proximoSeguimiento en cliente)
  const [modalCliente,  setModalCliente]  = useState(null);
  const [cliTipo,       setCliTipo]       = useState("Llamada");
  const [cliDesc,       setCliDesc]       = useState("");
  const [cliResultado,  setCliResultado]  = useState("");
  const [cliNuevaFecha, setCliNuevaFecha] = useState("");
  const [cliGuardando,  setCliGuardando]  = useState(false);


  const clienteMap = useMemo(() => {
    const map = {};
    clientes.forEach(c => { map[c.id] = c; });
    return map;
  }, [clientes]);

  const responsables = useMemo(() => {
    const set = new Set(cotizaciones.map(c => c.responsable).filter(Boolean));
    return [...set].sort();
  }, [cotizaciones]);

  // Cotizaciones con seguimiento activo
  const conDias = useMemo(() =>
    cotizaciones
      .filter(c => !["Ganada", "Cerrada", "Aprobada", "Perdida", "Rechazada", "Vencida"].includes(c.estado) && c.proximoSeguimiento && c.proximoSeguimiento !== "")
      .map(c => ({ ...c, _dias: calcDias(c.proximoSeguimiento) })),
    [cotizaciones]
  );

  // ── Pausar seguimiento (feature 001) ──────────────────────────────────────
  const DIAS_INACTIVIDAD_ALERTA = 15;

  // Última actividad comercial por cliente (cotización o pedido más reciente) — calculado
  // en vivo desde datos ya sincronizados, sin persistir un campo nuevo (data-model.md)
  const ultimaActividadPorCliente = useMemo(() => {
    const mapa = new Map();
    const considerar = (clienteId, fecha) => {
      const fObj = fechaAObjeto(fecha);
      if (!clienteId || !fObj) return;
      const actual = mapa.get(clienteId);
      if (!actual || fObj > actual) mapa.set(clienteId, fObj);
    };
    cotizaciones.forEach(c => considerar(c.clienteId, c.creadoEn));
    pedidos.forEach(p => considerar(p.clienteId, p.creadoEn));
    return mapa;
  }, [cotizaciones, pedidos]);

  // FR-004: cliente pausado, sin fecha agendada propia, con más de 15 días sin actividad
  const requiereAtencion = useCallback((cliente) => {
    if (!cliente.seguimientoPausado) return false;
    if (cliente.proximoSeguimiento && cliente.proximoSeguimiento !== "") return false; // FR-009
    const ultima = ultimaActividadPorCliente.get(cliente.id) || fechaAObjeto(cliente.seguimientoPausadoEn);
    if (!ultima) return false; // sin ninguna referencia, no se puede evaluar
    const dias = diasTranscurridos(ultima);
    return dias != null && dias > DIAS_INACTIVIDAD_ALERTA;
  }, [ultimaActividadPorCliente]);

  // Clientes con próximo seguimiento — fuente única de verdad
  // Pausados: solo aparecen si tienen fecha agendada explícita (FR-009) o si "requieren
  // atención" por inactividad (FR-002/FR-004) — nunca por el ruido normal de seguimiento.
  const clientesConSeg = useMemo(() =>
    clientes
      .filter(c => {
        const tieneFecha = c.proximoSeguimiento && c.proximoSeguimiento !== "";
        if (c.seguimientoPausado) return tieneFecha || requiereAtencion(c);
        return tieneFecha;
      })
      .map(c => ({
        ...c,
        _dias: c.proximoSeguimiento ? calcDias(c.proximoSeguimiento) : null,
        _requiereAtencion: !!(c.seguimientoPausado && !c.proximoSeguimiento && requiereAtencion(c)),
      }))
      .sort((a, b) => (a.proximoSeguimiento || "9999").localeCompare(b.proximoSeguimiento || "9999")),
    [clientes, requiereAtencion]
  );

  const cliVencidos = useMemo(() => clientesConSeg.filter(c => c._dias < 0), [clientesConSeg]);
  const cliHoy      = useMemo(() => clientesConSeg.filter(c => c._dias === 0), [clientesConSeg]);
  const cliProximos = useMemo(() => clientesConSeg.filter(c => c._dias > 0 && c._dias <= 7), [clientesConSeg]);

  const cliConteos = useMemo(() => ({
    total:         clientesConSeg.length,
    vencidos:      cliVencidos.length,
    hoy:           cliHoy.length,
    proximos:      cliProximos.length,
    valorTotal:    clientesConSeg.reduce((s, c) => s + (Number(c.valor) || 0), 0),
    valorVencidos: cliVencidos.reduce((s, c) => s + (Number(c.valor) || 0), 0),
    valorHoy:      cliHoy.reduce((s, c) => s + (Number(c.valor) || 0), 0),
    valorProximos: cliProximos.reduce((s, c) => s + (Number(c.valor) || 0), 0),
  }), [clientesConSeg, cliVencidos, cliHoy, cliProximos]);

  const conteos = useMemo(() => ({
    total:         conDias.length,
    vencidos:      conDias.filter(c => c._dias < 0).length,
    hoy:           conDias.filter(c => c._dias === 0).length,
    proximos:      conDias.filter(c => c._dias > 0 && c._dias <= 7).length,
    valorTotal:    conDias.reduce((s, c) => s + (Number(c.valor) || 0), 0),
    valorVencidos: conDias.filter(c => c._dias < 0).reduce((s, c) => s + (Number(c.valor) || 0), 0),
    valorHoy:      conDias.filter(c => c._dias === 0).reduce((s, c) => s + (Number(c.valor) || 0), 0),
    valorProximos: conDias.filter(c => c._dias > 0 && c._dias <= 7).reduce((s, c) => s + (Number(c.valor) || 0), 0),
  }), [conDias]);

  const filtrados = useMemo(() => {
    let lista = [...conDias];
    if (filtroTiempo === "vencidos")      lista = lista.filter(c => c._dias < 0);
    else if (filtroTiempo === "hoy")      lista = lista.filter(c => c._dias === 0);
    else if (filtroTiempo === "proximos") lista = lista.filter(c => c._dias > 0 && c._dias <= 7);
    if (filtroEstado !== "todos")         lista = lista.filter(c => c.estado === filtroEstado);
    if (filtroResponsable !== "todos")    lista = lista.filter(c => c.responsable === filtroResponsable);
    if (filtroTag) lista = lista.filter(c => (clienteMap[c.clienteId]?.tags || []).includes(filtroTag));
    if (busqueda) {
      const b = busqueda.toLowerCase();
      lista = lista.filter(c =>
        c.clienteNombre?.toLowerCase().includes(b) ||
        c.clienteEmpresa?.toLowerCase().includes(b) ||
        c.proyecto?.toLowerCase().includes(b) ||
        c.numeroCot?.toLowerCase().includes(b)
      );
    }
    return lista.sort((a, b) => (a.proximoSeguimiento || "").localeCompare(b.proximoSeguimiento || ""));
  }, [conDias, filtroTiempo, filtroEstado, filtroResponsable, busqueda, filtroTag, clienteMap]);

  // Tags únicos disponibles en todos los clientes
  const todosLosTagsDisp = useMemo(() => {
    const set = new Set();
    clientes.forEach(c => (c.tags || []).forEach(t => set.add(t)));
    return [...set].sort();
  }, [clientes]);

  const clientesFiltrados = useMemo(() => {
    let lista = clientesConSeg;
    if (filtroTag) lista = lista.filter(c => (c.tags || []).includes(filtroTag));
    if (busqueda && tabPrincipal === "clientes") {
      const b = busqueda.toLowerCase();
      lista = lista.filter(c => c.nombre?.toLowerCase().includes(b) || c.empresa?.toLowerCase().includes(b));
    }
    return lista;
  }, [clientesConSeg, busqueda, tabPrincipal, filtroTag]);

  useEffect(() => { setPaginaCot(1); }, [filtroTiempo, filtroEstado, filtroResponsable, busqueda]);

  // ── Handlers cotización ──
  const abrirRealizado = (cot) => { setNuevaFecha(""); setNotaRealizado(""); setModalRealizado(cot); };

  const confirmarRealizado = async () => {
    if (!modalRealizado) return;
    const hoy = fechaHoyLocal();

    // 1. Nota en la cotización (historial de notas)
    if (notaRealizado.trim()) {
      await agregarNotaCotizacion({
        cotizacionId:  modalRealizado.id,
        cotizacionNum: modalRealizado.numeroCot,
        clienteNombre: modalRealizado.clienteNombre,
        texto:         notaRealizado.trim(),
        tipo:          "Seguimiento",
      });
    }

    // 2. Actualizar seguimiento de la cotización
    await actualizarCotizacion(modalRealizado.id, {
      proximoSeguimiento: nuevaFecha || "",
      ultimoContacto:     hoy,
    });

    // 3. Registrar actividad en el perfil del cliente (fuente de verdad del historial)
    if (modalRealizado.clienteId) {
      await crearActividadCliente({
        clienteId:       modalRealizado.clienteId,
        clienteNombre:   modalRealizado.clienteNombre  || "",
        clienteEmpresa:  modalRealizado.clienteEmpresa || "",
        responsable:     modalRealizado.responsable    || "",
        tipo:            "Llamada",
        fecha:           hoy,
        descripcion:     notaRealizado.trim() || `Seguimiento cotización ${modalRealizado.numeroCot || ""}`,
        resultado:       "",
        proximoContacto: nuevaFecha || "",
      });
    }

    setModalRealizado(null);
    setNuevaFecha("");
    setNotaRealizado("");
  };

  // ── Handlers cliente ──
  const abrirClienteRealizado = (cli) => {
    setCliTipo("Llamada");
    setCliDesc("");
    setCliResultado("");
    setCliNuevaFecha("");
    setModalCliente(cli);
  };

  const confirmarClienteRealizado = async () => {
    if (!modalCliente) return;
    setCliGuardando(true);
    const hoy = fechaHoyLocal();
    try {
      // Crear actividad (log histórico)
      await crearActividadCliente({
        clienteId:      modalCliente.id,
        clienteNombre:  modalCliente.nombre,
        clienteEmpresa: modalCliente.empresa || "",
        responsable:    modalCliente.responsable || "",
        tipo:           cliTipo,
        fecha:          hoy,
        descripcion:    cliDesc || `Contacto por ${cliTipo.toLowerCase()}`,
        resultado:      cliResultado,
        proximoContacto: cliNuevaFecha || "",
      });
      // Siempre actualizar el doc del cliente (incluso para borrar la fecha)
      await updateDoc(doc(db, "clientes", modalCliente.id), {
        proximoSeguimiento: cliNuevaFecha || "",
      });
      toast.success("✅ Seguimiento registrado");
    } catch {
      toast.error("Error al guardar");
    }
    setCliGuardando(false);
    setModalCliente(null);
  };

  const abrirWA = (telefono, nombre) => {
    if (!telefono) { window.alert("Sin teléfono registrado"); return; }
    const num = (telefono || "").replace(/\D/g, "");
    const pre = num.startsWith("51") ? num : `51${num}`;
    const msg = `Hola ${(nombre || "").split(" ")[0]}, te contacto para hacer seguimiento. ¿Cómo va el proyecto? 🏗`;
    window.open(`https://wa.me/${pre}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const abrirEmail = (email, nombre) => {
    if (!email) { window.alert("Sin email registrado"); return; }
    window.open(`mailto:${email}?subject=Seguimiento comercial&body=Hola ${(nombre || "").split(" ")[0]}, te escribo para hacer seguimiento.`, "_blank");
  };

  if (loading) return (
    <Cargando />
  );

  // ── Paginación cotizaciones (computada ANTES del JSX, sin IIFE) ──
  const COT_POR_PAG    = 20;
  const cotTotalPags   = Math.max(1, Math.ceil(filtrados.length / COT_POR_PAG));
  const cotPaginaReal  = Math.min(paginaCot, cotTotalPags);
  const cotVisibles    = filtrados.slice((cotPaginaReal - 1) * COT_POR_PAG, cotPaginaReal * COT_POR_PAG);

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 4px" }}>

      {/* Modal Realizado — Cotización */}
      {modalRealizado && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="modal-content" style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 420, width: "90%" }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1e293b", marginBottom: 6 }}>✓ Marcar como realizado</h3>
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
              <strong>{modalRealizado.clienteNombre}</strong>
              {modalRealizado.numeroCot && <> · <span style={{ color: "#3A3C7A" }}>{modalRealizado.numeroCot}</span></>}
            </p>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 5 }}>
                Próximo seguimiento (opcional)
              </label>
              <input type="date" value={nuevaFecha} onChange={e => setNuevaFecha(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>Vacío = quitar seguimiento pendiente</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 5 }}>
                Nota del contacto (opcional)
              </label>
              <textarea value={notaRealizado} onChange={e => setNotaRealizado(e.target.value)}
                placeholder="Ej: Confirmó interés, enviará contrato la próxima semana..." rows={3}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, resize: "none", fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={confirmarRealizado} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: "#1E3A5F", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Confirmar
              </button>
              <button onClick={() => setModalRealizado(null)} style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 13, cursor: "pointer", color: "#475569" }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Realizado — Cliente */}
      {modalCliente && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="modal-content" style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 480, width: "90%" }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1e293b", marginBottom: 6 }}>✓ Registrar contacto</h3>
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
              <strong>{modalCliente.nombre}</strong>
              {modalCliente.empresa && <> · <span style={{ color: "#64748b" }}>{modalCliente.empresa}</span></>}
            </p>

            {/* Tipo */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 6 }}>Tipo de contacto</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {TIPOS_ACTIVIDAD.map(t => (
                  <button key={t.key} type="button" onClick={() => setCliTipo(t.key)} style={{
                    fontSize: 11, fontWeight: 600, padding: "5px 10px", borderRadius: 8, cursor: "pointer", border: "1.5px solid",
                    borderColor: cliTipo === t.key ? "#7C3AED" : "#e2e8f0",
                    background:  cliTipo === t.key ? "#EDE9FE" : "#fff",
                    color:       cliTipo === t.key ? "#7C3AED" : "#475569",
                  }}>
                    {t.icon} {t.key}
                  </button>
                ))}
              </div>
            </div>

            {/* Descripción */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 5 }}>¿Qué pasó?</label>
              <textarea value={cliDesc} onChange={e => setCliDesc(e.target.value)}
                placeholder="Ej: Llamé al cliente, confirmó interés en la cotización..."
                rows={2} style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, resize: "none", fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
            </div>

            {/* Resultado */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 5 }}>Resultado / acuerdo</label>
              <input value={cliResultado} onChange={e => setCliResultado(e.target.value)}
                placeholder="Ej: Enviar cotización la próxima semana"
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
            </div>

            {/* Próxima fecha */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 5 }}>
                Próximo seguimiento
              </label>
              <input type="date" value={cliNuevaFecha} onChange={e => setCliNuevaFecha(e.target.value)}
                min={fechaHoyLocal()}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>Vacío = quitar seguimiento pendiente del cliente</div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={confirmarClienteRealizado} disabled={cliGuardando} style={{
                flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: "#7C3AED", color: "#fff",
                fontSize: 13, fontWeight: 700, cursor: cliGuardando ? "not-allowed" : "pointer", opacity: cliGuardando ? 0.7 : 1,
              }}>
                {cliGuardando ? "Guardando..." : "Confirmar"}
              </button>
              <button onClick={() => setModalCliente(null)} style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 13, cursor: "pointer", color: "#475569" }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b" }}>Seguimiento</h1>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            {tabPrincipal === "cotizaciones"
              ? <>{filtrados.length} cotización{filtrados.length !== 1 ? "es" : ""} · Pipeline vencido: <strong style={{ color: "#DC2626" }}>{fmtMoney(conteos.valorVencidos)}</strong></>
              : <>{clientesFiltrados.length} cliente{clientesFiltrados.length !== 1 ? "s" : ""} con seguimiento pendiente</>
            }
          </div>
        </div>
      </div>

      {/* Tabs principales */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        <button onClick={() => setTabPrincipal("cotizaciones")} style={{
          padding: "8px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer",
          background: tabPrincipal === "cotizaciones" ? "#1E3A5F" : "#f1f5f9",
          color:      tabPrincipal === "cotizaciones" ? "#fff"    : "#64748b",
        }}>
          📋 Cotizaciones ({conteos.total})
        </button>
        <button onClick={() => setTabPrincipal("clientes")} style={{
          padding: "8px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer",
          background: tabPrincipal === "clientes" ? "#7C3AED" : "#f1f5f9",
          color:      tabPrincipal === "clientes" ? "#fff"    : "#64748b",
          position: "relative",
        }}>
          👥 Clientes ({clientesConSeg.length})
          {cliVencidos.length > 0 && (
            <span style={{ position: "absolute", top: -4, right: -4, background: "#DC2626", color: "#fff", fontSize: 9, fontWeight: 700, borderRadius: "50%", width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {cliVencidos.length}
            </span>
          )}
        </button>
      </div>

      {/* ── TAB CLIENTES ── */}
      {tabPrincipal === "clientes" ? (
        <ClientesSeguimientoTab
          clientes={clientesFiltrados}
          conteos={cliConteos}
          busqueda={busqueda}
          setBusqueda={setBusqueda}
          onRealizado={abrirClienteRealizado}
          onWA={abrirWA}
          onEmail={abrirEmail}
          actualizarCliente={actualizarCliente}
        />
      ) : (<>

      {/* KPI chips de tiempo — filtro + métrica combinados */}
      <style>{`.seg-chip{transition:background 150ms cubic-bezier(0.23,1,0.32,1),box-shadow 150ms cubic-bezier(0.23,1,0.32,1),transform 100ms cubic-bezier(0.23,1,0.32,1)}.seg-chip:active{transform:scale(0.96)!important}.seg-chip:hover{filter:brightness(0.95)}`}</style>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
        {[
          { key: "todos",    label: "Todos",       count: conteos.total,    valor: conteos.valorTotal,    color: "#1E3A5F" },
          { key: "vencidos", label: "Vencidos",    count: conteos.vencidos, valor: conteos.valorVencidos, color: "#DC2626" },
          { key: "hoy",      label: "Hoy",         count: conteos.hoy,      valor: conteos.valorHoy,      color: "#D97706" },
          { key: "proximos", label: "Próximos 7d", count: conteos.proximos, valor: conteos.valorProximos, color: "#1D9E75" },
        ].map(f => {
          const activo = filtroTiempo === f.key;
          return (
            <button key={f.key} className="seg-chip" onClick={() => { setFiltroTiempo(f.key); setPaginaCot(1); }} style={{
              background: activo ? f.color : "#fff", color: activo ? "#fff" : f.color,
              border: `1.5px solid ${activo ? f.color : "#e2e8f0"}`, borderRadius: 12,
              padding: "12px 8px", cursor: "pointer", textAlign: "center",
              boxShadow: activo ? `0 2px 10px ${f.color}44` : "none",
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3, opacity: activo ? 0.9 : 0.7 }}>{f.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{f.count}</div>
              <div style={{ fontSize: 9, marginTop: 4, opacity: activo ? 0.9 : 0.7 }}>S/ {(f.valor / 1000).toFixed(0)}k</div>
            </button>
          );
        })}
      </div>

      {/* Filtros secundarios */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <input
          placeholder="Buscar cliente, empresa, proyecto, N°..."
          value={busqueda} onChange={e => setBusqueda(e.target.value)}
          style={{ flex: 1, minWidth: 180, padding: "7px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, outline: "none" }}
        />
        {ESTADOS_COT.map(est => {
          const on = filtroEstado === est;
          const s  = ESTADO_STYLE[est];
          return (
            <button key={est} onClick={() => setFiltroEstado(on ? "todos" : est)} style={{
              padding: "6px 14px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer",
              border: `1.5px solid ${on ? s.color : "#e2e8f0"}`,
              background: on ? s.bg : "#fff", color: on ? s.color : "#94a3b8",
            }}>{est}</button>
          );
        })}
        {responsables.length > 0 && (
          <select value={filtroResponsable} onChange={e => setFiltroResponsable(e.target.value)}
            style={{ padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 11, color: "#475569", background: "#fff", cursor: "pointer" }}>
            <option value="todos">Asesor: Todos</option>
            {responsables.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        )}
        {todosLosTagsDisp.length > 0 && (
          <select value={filtroTag} onChange={e => setFiltroTag(e.target.value)}
            style={{ padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 11, color: filtroTag ? "#1d4ed8" : "#475569", background: filtroTag ? "#eff6ff" : "#fff", cursor: "pointer", fontWeight: filtroTag ? 600 : 400 }}>
            <option value="">Tag: Todos</option>
            {todosLosTagsDisp.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {(busqueda || filtroEstado !== "todos" || filtroResponsable !== "todos" || filtroTag) && (
          <button onClick={() => { setBusqueda(""); setFiltroEstado("todos"); setFiltroResponsable("todos"); setFiltroTag(""); }}
            style={{ padding: "5px 10px", border: "none", borderRadius: 8, fontSize: 11, color: "#DC2626", background: "#FCEBEB", cursor: "pointer", fontWeight: 600 }}>
            × Limpiar
          </button>
        )}
      </div>

      <div>
        {filtrados.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 20px", background: "#fff", borderRadius: 12, border: "0.5px solid #e2e8f0", color: "#94a3b8", fontSize: 13 }}>
            No hay seguimientos con este filtro.
          </div>
        ) : (
          <>
            {cotVisibles.map(cot => (
              <CotizacionCard
                key={cot.id}
                cot={cot}
                cliente={clienteMap[cot.clienteId]}
                onWA={abrirWA}
                onEmail={abrirEmail}
                onRealizado={abrirRealizado}
              />
            ))}
            <BarraPaginasSeg total={filtrados.length} porPagina={COT_POR_PAG} pagina={cotPaginaReal} onChange={setPaginaCot} />
          </>
        )}
      </div>
      </>)}

    </div>
  );
}

function BarraPaginasSeg({ total, porPagina, pagina, onChange }) {
  if (total <= porPagina) return null;
  const totalPags = Math.ceil(total / porPagina);
  const desde = (pagina - 1) * porPagina + 1;
  const hasta  = Math.min(pagina * porPagina, total);
  const nums = [];
  for (let i = 1; i <= totalPags; i++) {
    if (i === 1 || i === totalPags || (i >= pagina - 1 && i <= pagina + 1)) nums.push(i);
    else if (nums[nums.length - 1] !== "...") nums.push("...");
  }
  const btn = { padding: "5px 12px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12, fontWeight: 500, cursor: "pointer" };
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", marginTop: 8 }}>
      <span style={{ fontSize: 12, color: "#64748b" }}>Mostrando {desde} a {hasta} de {total} registros</span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button onClick={() => onChange(pagina - 1)} disabled={pagina === 1}
          style={{ ...btn, color: pagina === 1 ? "#d1d5db" : "#475569", cursor: pagina === 1 ? "not-allowed" : "pointer" }}>Anterior</button>
        {nums.map((n, i) => n === "..." ? (
          <span key={`e${i}`} style={{ fontSize: 12, color: "#94a3b8" }}>…</span>
        ) : (
          <button key={n} onClick={() => onChange(n)} style={{
            width: 32, height: 32, borderRadius: 6, border: "1px solid", fontSize: 12, fontWeight: 600, cursor: "pointer",
            borderColor: pagina === n ? "#1E3A5F" : "#e2e8f0",
            background:  pagina === n ? "#1E3A5F" : "#fff",
            color:       pagina === n ? "#fff"    : "#475569",
          }}>{n}</button>
        ))}
        <button onClick={() => onChange(pagina + 1)} disabled={pagina === totalPags}
          style={{ ...btn, color: pagina === totalPags ? "#d1d5db" : "#475569", cursor: pagina === totalPags ? "not-allowed" : "pointer" }}>Siguiente</button>
      </div>
    </div>
  );
}

// ── Tab seguimiento de clientes ──────────────────────────────────────────────
function ClientesSeguimientoTab({ clientes, conteos, busqueda, setBusqueda, onRealizado, onWA, onEmail, actualizarCliente }) {
  const [filtroActivo, setFiltroActivo] = useState("todos");
  const [pagina,       setPagina]       = useState(1);

  const mostrar = clientes.filter(c => {
    if (filtroActivo === "vencidos")  return c._dias < 0;
    if (filtroActivo === "hoy")       return c._dias === 0;
    if (filtroActivo === "proximos")  return c._dias > 0 && c._dias <= 7;
    return true;
  });

  const POR_PAG   = 20;
  const totalPags = Math.max(1, Math.ceil(mostrar.length / POR_PAG));
  const paginaVal = Math.min(pagina, totalPags);
  const visibles  = mostrar.slice((paginaVal - 1) * POR_PAG, paginaVal * POR_PAG);

  return (
    <div>
      <style>{`.cli-chip{transition:background 150ms cubic-bezier(0.23,1,0.32,1),box-shadow 150ms cubic-bezier(0.23,1,0.32,1),transform 100ms cubic-bezier(0.23,1,0.32,1)}.cli-chip:active{transform:scale(0.96)!important}.cli-chip:hover{filter:brightness(0.95)}`}</style>

      {/* KPI chips — filtro + métrica */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
        {[
          { key: "todos",    label: "Todos",       count: conteos.total,    valor: conteos.valorTotal,    color: "#7C3AED" },
          { key: "vencidos", label: "Vencidos",    count: conteos.vencidos, valor: conteos.valorVencidos, color: "#DC2626" },
          { key: "hoy",      label: "Hoy",         count: conteos.hoy,      valor: conteos.valorHoy,      color: "#D97706" },
          { key: "proximos", label: "Próximos 7d", count: conteos.proximos, valor: conteos.valorProximos, color: "#1D9E75" },
        ].map(f => {
          const activo = filtroActivo === f.key;
          return (
            <button key={f.key} className="cli-chip" onClick={() => { setFiltroActivo(f.key); setPagina(1); }} style={{
              background: activo ? f.color : "#fff", color: activo ? "#fff" : f.color,
              border: `1.5px solid ${activo ? f.color : "#e2e8f0"}`, borderRadius: 12,
              padding: "12px 8px", cursor: "pointer", textAlign: "center",
              boxShadow: activo ? `0 2px 10px ${f.color}44` : "none",
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3, opacity: activo ? 0.9 : 0.7 }}>{f.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{f.count}</div>
              <div style={{ fontSize: 9, marginTop: 4, opacity: activo ? 0.9 : 0.7 }}>S/ {(f.valor / 1000).toFixed(0)}k</div>
            </button>
          );
        })}
      </div>

      {/* Buscador */}
      <input
        placeholder="Buscar por nombre o empresa..."
        value={busqueda} onChange={e => setBusqueda(e.target.value)}
        style={{ width: "100%", padding: "8px 14px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none", marginBottom: 12, boxSizing: "border-box" }}
      />

      {mostrar.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", background: "#fff", borderRadius: 12, border: "0.5px solid #e2e8f0", color: "#94a3b8", fontSize: 13 }}>
          No hay clientes con seguimiento pendiente en este filtro.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {visibles.map(c => {
            const vencido = c._dias < 0;
            const esHoy   = c._dias === 0;
            const franjaColor = c._requiereAtencion ? "#D97706" : vencido ? "#DC2626" : esHoy ? "#D97706" : "#7C3AED";
            const fbg  = vencido ? "#FCEBEB" : esHoy ? "#FAEEDA" : "#E1F5EE";
            const fcol = vencido ? "#A32D2D" : esHoy ? "#854F0B" : "#0F6E56";
            const av   = avColor(c.nombre);
            return (
              <div key={c.id} style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 12, overflow: "hidden", display: "flex" }}>
                <div style={{ width: 4, flexShrink: 0, background: franjaColor }} />
                <div style={{ flex: 1, padding: "12px 16px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>

                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: av.bg, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                    {(c.nombre || "?").charAt(0).toUpperCase()}
                  </div>

                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 2 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{c.nombre}</span>
                      {c.estado && (
                        <span style={{ fontSize: 10, fontWeight: 600, background: "#f1f5f9", color: "#475569", padding: "1px 7px", borderRadius: 5 }}>
                          {c.estado}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      {c.empresa || ""}{c.ciudad ? ` · ${c.ciudad}` : ""}{c.responsable ? ` · ${c.responsable}` : ""}
                    </div>
                    {c.proyecto && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>🏗 {c.proyecto}</div>}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, minWidth: 90 }}>
                    {c._requiereAtencion ? (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 10, background: "#FAEEDA", color: "#854F0B", textAlign: "center" }}>
                        ⚠ Pausado — requiere atención
                      </span>
                    ) : (
                      <>
                        <span style={{ fontSize: 12, fontWeight: 500, color: "#1e293b" }}>{fmtFecha(c.proximoSeguimiento)}</span>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: fbg, color: fcol }}>
                          {labelDias(c._dias)}
                        </span>
                        {c.seguimientoPausado && (
                          <span style={{ fontSize: 9, fontWeight: 600, color: "#94a3b8" }}>⏸ Pausado</span>
                        )}
                      </>
                    )}
                  </div>

                  {c.valor > 0 && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", minWidth: 80, textAlign: "right" }}>
                      S/ {Number(c.valor).toLocaleString("es-PE")}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
                    <button onClick={() => onWA(c.telefono, c.nombre)}
                      style={{ fontSize: 11, fontWeight: 600, padding: "6px 12px", borderRadius: 8, background: "#DCF8C6", color: "#128C7E", border: "none", cursor: "pointer" }}>
                      WhatsApp
                    </button>
                    <button onClick={() => onEmail(c.email, c.nombre)}
                      style={{ fontSize: 11, fontWeight: 600, padding: "6px 12px", borderRadius: 8, background: "#E6F1FB", color: "#185FA5", border: "none", cursor: "pointer" }}>
                      Email
                    </button>
                    <button onClick={() => onRealizado(c)}
                      style={{ fontSize: 11, fontWeight: 600, padding: "6px 12px", borderRadius: 8, background: "#7C3AED", color: "#fff", border: "none", cursor: "pointer" }}>
                      ✓ Realizado
                    </button>
                    <Link to={`/clientes/${c.id}/perfil`}
                      style={{ fontSize: 11, fontWeight: 600, padding: "6px 12px", borderRadius: 8, background: "#EDE9FE", color: "#7C3AED", textDecoration: "none" }}>
                      360°
                    </Link>
                    {c.seguimientoPausado ? (
                      <button onClick={() => actualizarCliente(c.id, { seguimientoPausado: false, seguimientoPausadoEn: null })}
                        title="Reactivar seguimiento normal para este cliente"
                        style={{ fontSize: 11, fontWeight: 600, padding: "6px 12px", borderRadius: 8, background: "#E1F5EE", color: "#0F6E56", border: "none", cursor: "pointer" }}>
                        ▶ Reactivar
                      </button>
                    ) : (
                      <button onClick={() => actualizarCliente(c.id, { seguimientoPausado: true, seguimientoPausadoEn: fechaHoyLocal() })}
                        title="Pausar seguimiento automático — útil para clientes recurrentes"
                        style={{ fontSize: 11, fontWeight: 600, padding: "6px 12px", borderRadius: 8, background: "#f1f5f9", color: "#475569", border: "none", cursor: "pointer" }}>
                        ⏸ Pausar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <BarraPaginasSeg total={mostrar.length} porPagina={POR_PAG} pagina={paginaVal} onChange={setPagina} />
    </div>
  );
}
